import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// HighlightCard – gradient-background card for benefit/feature highlights
// ---------------------------------------------------------------------------

/**
 * Color theme options for highlight cards
 */
export type HighlightCardColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'indigo'
  | 'pink'
  | 'rose';

interface HighlightCardSignature {
  Args: {
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Prominent value (e.g., "$5,250/year", "AI-Powered") */
    value: string;
    /** Description text */
    description?: string;
    /** Color theme for gradient and icon */
    color?: HighlightCardColor;
  };
  Blocks: {
    /** Optional extra content at the bottom */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<
  HighlightCardColor,
  { gradient: string; border: string; iconBg: string; iconText: string; valueText: string }
> = {
  emerald: {
    gradient: 'from-emerald-50 to-teal-50 dark:from-emerald-950 dark:to-teal-950',
    border: 'border-emerald-100 dark:border-emerald-800',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconText: 'text-emerald-600 dark:text-emerald-400',
    valueText: 'text-emerald-600 dark:text-emerald-400',
  },
  teal: {
    gradient: 'from-teal-50 to-cyan-50 dark:from-teal-950 dark:to-cyan-950',
    border: 'border-teal-100 dark:border-teal-800',
    iconBg: 'bg-teal-100 dark:bg-teal-900/50',
    iconText: 'text-teal-600 dark:text-teal-400',
    valueText: 'text-teal-600 dark:text-teal-400',
  },
  blue: {
    gradient: 'from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950',
    border: 'border-blue-100 dark:border-blue-800',
    iconBg: 'bg-blue-100 dark:bg-blue-900/50',
    iconText: 'text-blue-600 dark:text-blue-400',
    valueText: 'text-blue-600 dark:text-blue-400',
  },
  indigo: {
    gradient: 'from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950',
    border: 'border-indigo-100 dark:border-indigo-800',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    iconText: 'text-indigo-600 dark:text-indigo-400',
    valueText: 'text-indigo-600 dark:text-indigo-400',
  },
  violet: {
    gradient: 'from-violet-50 to-purple-50 dark:from-violet-950 dark:to-purple-950',
    border: 'border-violet-100 dark:border-violet-800',
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconText: 'text-violet-600 dark:text-violet-400',
    valueText: 'text-violet-600 dark:text-violet-400',
  },
  amber: {
    gradient: 'from-amber-50 to-orange-50 dark:from-amber-950 dark:to-orange-950',
    border: 'border-amber-100 dark:border-amber-800',
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconText: 'text-amber-600 dark:text-amber-400',
    valueText: 'text-amber-600 dark:text-amber-400',
  },
  pink: {
    gradient: 'from-pink-50 to-rose-50 dark:from-pink-950 dark:to-rose-950',
    border: 'border-pink-100 dark:border-pink-800',
    iconBg: 'bg-pink-100 dark:bg-pink-900/50',
    iconText: 'text-pink-600 dark:text-pink-400',
    valueText: 'text-pink-600 dark:text-pink-400',
  },
  rose: {
    gradient: 'from-rose-50 to-pink-50 dark:from-rose-950 dark:to-pink-950',
    border: 'border-rose-100 dark:border-rose-800',
    iconBg: 'bg-rose-100 dark:bg-rose-900/50',
    iconText: 'text-rose-600 dark:text-rose-400',
    valueText: 'text-rose-600 dark:text-rose-400',
  },
};

const getColorClasses = (color: HighlightCardColor = 'emerald') => colorClasses[color];

/**
 * HighlightCard - A gradient-background card for benefit/feature highlights
 *
 * Used to showcase key benefits or features with a soft gradient background,
 * colored icon badge, title, prominent value, and description.
 *
 * @example
 * ```gts
 * import HighlightCard from 'tio-reimagined/components/highlight-card';
 *
 * <HighlightCard
 *   @icon={{PhGraduationCap}}
 *   @title="Tuition Assistance"
 *   @value="$5,250/year"
 *   @description="Tax-free education funding for degrees and certificates"
 *   @color="emerald"
 * />
 * ```
 */
const HighlightCard: TOC<HighlightCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <div
      data-test-highlight-card
      class="bg-gradient-to-br {{colors.gradient}} rounded-xl p-5 border {{colors.border}}"
      ...attributes
    >
      <div class="p-2 {{colors.iconBg}} rounded-lg w-fit mb-3">
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} class={{colors.iconText}} />
        {{/let}}
      </div>
      <div data-test-highlight-card-title class="font-semibold text-gray-900 dark:text-white mb-1">
        {{@title}}
      </div>
      <div data-test-highlight-card-value class="text-2xl font-bold {{colors.valueText}} mb-2">
        {{@value}}
      </div>
      {{#if @description}}
        <Text
          data-test-highlight-card-description
          @intent="muted"
          class="text-sm dark:text-gray-400"
        >
          {{@description}}
        </Text>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-3">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default HighlightCard;

export type { HighlightCardSignature };
